import {
    IconButton,
    Typography,
    Stack,
    Autocomplete,
    TextField,
} from "@mui/material";
import { useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeftIcon, ArrowRightIcon } from "@/ImageRepo";
import { selectSelectedSchema } from "@/features/mapSchema/mapSchemaSlice";
import {
    selectArtifactCount,
    selectSelectedArtifactIndex,
    selectIsLoading,
    selectArtifactData,
    fetchArtifactPreview,
    decrementAndUpdateArtifactPreview,
    incrementAndUpdateArtifactPreview,
    selectSelectedArtifactReference,
    setSelectedArtifactReference,
    selectArtifactReferenceOptions,
    selectAutocompleteOptions,
    selectIsSearching,
    searchArtifactReferences,
    setSelectedArtifactIndex,
    setArtifactData,
    fetchArtifactReferenceByIndex,
    appendArtifactReferenceOption,
} from "@/features/connections/artifactPreviewSlice";
import LoadingBar, { LoadingAnimation } from "@/components/shared/LoadingBar";
import { ShepButton } from "@/components/GenericComponents/ShepComponents";
import ReactCodeMirror, { placeholder } from "@uiw/react-codemirror";
import { EditorView } from "@codemirror/view";

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;
const MAX_NAV_WINDOW = 10; //Auto displayed records are capped for ease-of-use

const PreviewArtifact = ({
    connectionId,
    artifactType,
    templateElement,
    loopRow,
}) => {
    const dispatch = useDispatch();
    const artifactCount = useSelector(selectArtifactCount);
    const selectedArtifactIndex = useSelector(selectSelectedArtifactIndex);
    const isLoading = useSelector(selectIsLoading);
    const artifactData = useSelector(selectArtifactData);
    const artifactReference = useSelector(selectSelectedArtifactReference);
    const artifactReferenceOptions = useSelector(selectArtifactReferenceOptions);
    const autocompleteOptions = useSelector(selectAutocompleteOptions);
    const isSearching = useSelector(selectIsSearching);
    const selectedSchema = useSelector(selectSelectedSchema);

    const debounceTimerRef = useRef(null);


    // Use server-filtered autocomplete options when user has typed enough, otherwise show preloaded options
    const displayOptions = useMemo(() => {
        if ((artifactReference || "").length >= MIN_SEARCH_LENGTH) {
            return autocompleteOptions;
        }
        return artifactReferenceOptions;
    }, [artifactReference, autocompleteOptions, artifactReferenceOptions]);

    const debouncedSearch = useCallback(
        (searchText) => {
            // Clear any pending debounce
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            if (!searchText || searchText.length < MIN_SEARCH_LENGTH) {
                return;
            }

            debounceTimerRef.current = setTimeout(() => {
                dispatch(
                    searchArtifactReferences({
                        connectionId,
                        artifactType,
                        searchText,
                    })
                );
            }, DEBOUNCE_MS);
        },
        [dispatch, connectionId, artifactType]
    );

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const oldestNavigableIndex = Math.max(artifactCount - MAX_NAV_WINDOW, 0);
    const isOutsideWindow = selectedArtifactIndex < oldestNavigableIndex;

    const goNewer = async () => {
        if (isOutsideWindow) {
            const prevIndex = selectedArtifactIndex;
            const prevReference = artifactReference;
            const prevData = artifactData;
            await dispatch(setArtifactData({ artifactData: "" }));
            await dispatch(setSelectedArtifactIndex({ selectedArtifactIndex: oldestNavigableIndex }));
            const result = await dispatch(fetchArtifactReferenceByIndex({ connectionId, artifactType }));
            if (result.error) {
                await dispatch(setSelectedArtifactIndex({ selectedArtifactIndex: prevIndex }));
                await dispatch(setSelectedArtifactReference({ selectedArtifactReference: prevReference }));
                await dispatch(setArtifactData({ artifactData: prevData }));
            }
        } else {
            dispatch(incrementAndUpdateArtifactPreview({ connectionId, artifactType }));
        }
    };
    const goOlder = () => {
        if (isOutsideWindow || selectedArtifactIndex <= oldestNavigableIndex) {
            return;
        }
        dispatch(decrementAndUpdateArtifactPreview({ connectionId, artifactType }));
    };
    const disableSelect = artifactCount === 0 || isLoading;
    const displayNumber = artifactCount - selectedArtifactIndex;
    const displayTotal = Math.min(artifactCount, MAX_NAV_WINDOW);

    const selectOption = useCallback(
        (option) => {
            if (!option) return;
            const selectedIndex = artifactReferenceOptions.findIndex(
                (opt) => opt.ArtifactDictionaryId === option.ArtifactDictionaryId
            );
            dispatch(
                setSelectedArtifactReference({
                    selectedArtifactReference: option.ArtifactReference,
                })
            );
            if (selectedIndex >= 0) {
                dispatch(
                    setSelectedArtifactIndex({ selectedArtifactIndex: selectedIndex })
                );
            } else {
                dispatch(appendArtifactReferenceOption(option));
                dispatch(
                    setSelectedArtifactIndex({ selectedArtifactIndex: artifactReferenceOptions.length })
                );
            }
            dispatch(
                fetchArtifactPreview({
                    connectionId,
                    artifactType,
                    templateElement,
                    loopRow,
                    variantName: selectedSchema.VariantName,
                })
            );
        },
        [dispatch, artifactReferenceOptions, connectionId, artifactType, templateElement, loopRow, selectedSchema]
    );

    const handleAutocompleteChange = (_event, newValue) => {
        selectOption(newValue);
    };

    const selectedOption =
        displayOptions.find(
            (opt) => opt.ArtifactReference === artifactReference
        ) ||
        artifactReferenceOptions.find(
            (opt) => opt.ArtifactReference === artifactReference
        ) || null;

    return (
        <Stack
            direction="column"
            spacing={2}
            sx={{
                height: "100%",
                width: "100%",
                padding: "0 10px 0 20px",
                boxSizing: "border-box",
            }}
        >
            {/* Navigation Row */}
            <Stack
                flexDirection="row"
                justifyContent="space-evenly"
                alignItems="center"
                sx={{ width: "100%", gap: "15px" }}
            >
                <Stack flexDirection="row" alignItems="center">
                    <IconButton
                        onClick={goNewer}
                        style={{
                            padding: 0,
                            backgroundColor: "transparent",
                        }}
                        disabled={disableSelect}
                    >
                        <ArrowLeftIcon
                            style={{ color: "red", width: "30px", height: "30px" }}
                        />
                    </IconButton>

                    <Typography
                        sx={{
                            textAlign: "center",
                            fontSize: "14px",
                            padding: "0 20px",
                        }}
                    >
                        {`${displayNumber} of ${displayTotal} (Ordered by date created)`}
                    </Typography>

                    <IconButton
                        onClick={goOlder}
                        style={{
                            padding: 0,
                            backgroundColor: "transparent",
                        }}
                        disabled={disableSelect}
                    >
                        <ArrowRightIcon
                            style={{ color: "red", width: "30px", height: "30px" }}
                        />
                    </IconButton>
                </Stack>
                <ShepButton
                    className="btn-secondary"
                    disabled={disableSelect}
                    onClick={() => {
                        dispatch(
                            fetchArtifactPreview({
                                connectionId,
                                artifactType,
                                templateElement,
                                loopRow,
                                variantName: selectedSchema.VariantName,
                            })
                        );
                    }}
                >
                    Preview
                </ShepButton>
            </Stack>

            {/* Artifact Reference + Preview Row */}
            <Stack
                flexDirection="row"
                justifyContent="center"
                alignItems="center"
                sx={{ width: "100%" }}
            >
                <Typography sx={{ whiteSpace: "nowrap" }}>
                    Artifact Reference:
                </Typography>
                <Autocomplete
                    freeSolo
                    disabled={disableSelect}
                    options={displayOptions}
                    filterOptions={(options) => options}
                    loading={isSearching}
                    getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.ArtifactReference || ""
                    }
                    value={selectedOption}
                    inputValue={artifactReference || ""}
                    onInputChange={(_event, newInputValue, reason) => {
                        if (reason === "input" || reason === "clear") {
                            dispatch(
                                setSelectedArtifactReference({
                                    selectedArtifactReference: newInputValue,
                                })
                            );
                            debouncedSearch(newInputValue);
                        }
                    }}
                    onChange={handleAutocompleteChange}
                    renderOption={(props, option) => (
                        <li
                            {...props}
                            key={option.ArtifactDictionaryId}
                            onClick={(e) => {
                                selectOption(option);
                                if (props.onClick) props.onClick(e);
                            }}
                        >
                            {option.ArtifactReference}
                        </li>
                    )}
                    isOptionEqualToValue={(option, value) =>
                        option.ArtifactDictionaryId === value?.ArtifactDictionaryId
                    }
                    sx={{ flex: 1, margin: "0 0 0 20px" }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="standard"
                            inputProps={{
                                ...params.inputProps,
                                maxLength: 200,
                                title: artifactReference || "",
                            }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {isSearching ? (
                                            <LoadingAnimation size={20} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            </Stack>

            {/* Preview Area */}
            <Stack sx={{ flex: 1, width: "100%", minHeight: 0 }}>
                {isLoading && <LoadingBar isDataLoaded={!isLoading} />}
                {!isLoading && (
                    <ReactCodeMirror
                        value={artifactData}
                        readOnly={true}
                        style={{
                            border: "1px solid #ccc",
                        }}
                        extensions={[
                            placeholder('Click "Preview" to see document output'),
                            EditorView.lineWrapping,
                        ]}
                    />
                )}
            </Stack>
        </Stack>
    );
};

export default PreviewArtifact;
